import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { createFragmentContainer } from 'react-relay';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Cancel, CheckCircle, KeyboardArrowDown, KeyboardArrowUp, PauseCircleFilled } from '@mui/icons-material';
import { Collapse, Grid, IconButton, InputLabel, Link, TableContainer, Table,  TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography} from '@mui/material';
import { green, red, blue } from '@mui/material/colors';

import AppLogo from '../ui/AppLogo';

const styleSheet = theme => ({
    paper: {
      width: '100%',
      marginTop: theme.spacing(2),
      overflow: 'auto',
    },
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 32,
    },
    tblHeadCell: {
      textAlign: 'left',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },
    tblHeadCellB: {
      textAlign: 'right',
      verticalAlign: 'bottom',
    },
    tblHeadRow3: {
      height: 32,
    },
    markup: {
      color: '#666666'
    },
    appgrid: {
      minWidth: 136,
    },
    appnameitem: {
      marginLeft: 8,
    },
    inactive: {
      fontStyle: 'italic'
    }
});

function SubscriptionRow(props) {

  function goTo(route) {
    props.history.push(`/${route}`);
  }

  let { n, previousAppName, previousCustomerName, classes, resellerAccountId, getCurrencyString } = props;
  const [rowOpen, setRowOpen] = React.useState(false);
  
  const statusOk = green[300];
  const statusPause = blue[300];
  const statusCancel = red[300];

  let tmBcSum = getCurrencyString(n.tmBcSum);
  let tmRebateSum = getCurrencyString(n.tmRebateSum);
  let appName = n.appName;
  /* eslint-disable eqeqeq */
  if (n.appSku != null && n.appSku !== '' && n.appSku != n.appName) {
    appName = n.appName + " (" + n.appSku + ")";
  }

  let repeat = false;
  if ((previousAppName === appName) && (previousCustomerName === n.customerAccountName)) {
    repeat = true;
  } else {
    previousAppName = appName;
    previousCustomerName = n.customerAccountName;
  }

  let hasCommission = false;
  let theProgramLink = '';
  if (n.programName) {
    hasCommission = true;
    theProgramLink = 'resellerprogramedit?resellerPId=' + n.programId + '&accountId=' + resellerAccountId  + "&fp=Subscriptions";
  }

  let address = {
    address1: n.customerAddress1,
    address2: n.customerAddress2,
    city: n.customerCity,
    state: n.customerState,
    country: n.customerCountry,
    postalCode: n.customerPostalCode
  };

  let customerContactName;
  if (n.customerFirstName != null && n.customerLastName != null) {
    customerContactName = n.customerFirstName + ' ' + n.customerLastName;
  } else if (n.customerFirstName == null && n.customerLastName != null) {
    customerContactName = n.customerLastName;
  } else if (n.customerFirstName != null && n.customerLastName == null) {
    customerContactName = n.customerFirstName;
  } else {
    // no customer name, just use account name
    customerContactName = n.customerAccountName;
  }
  
  return (
    <React.Fragment>
      {/* <TableRow sx={ rowOpen ? { '& > td':{ borderBottom: '1px dashed rgba(224, 224, 224, 1)' }} : { '& > td':{ borderBottom: 'unset' } }}> */}
      <TableRow sx={{ '& > td':{ borderBottom: 'unset' } }}>
        <TableCell align="left">
          {!repeat && 
          <IconButton
            aria-label="row expand"
            size="small"
            onClick={() => setRowOpen(!rowOpen)}>
            { rowOpen ? <KeyboardArrowUp/> : <KeyboardArrowDown/> }
          </IconButton>
          }
        </TableCell>
        <TableCell align="left">
          {!repeat &&
            <Typography variant="body2">
              {n.customerAccountName}
            </Typography>
          }
        </TableCell>
        <TableCell>
          {!repeat &&
          <Grid container alignItems="center" wrap="nowrap" className={classes.appgrid} spacing={1}>
            <Grid item>
              <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px' style={{ display:'inline-flex', verticalAlign:'middle'}}/>
            </Grid>
            <Grid item>
              {appName}
            </Grid>
          </Grid>}
        </TableCell>
        <TableCell align="left">
          {hasCommission &&
          <Typography variant="body2">
            <Tooltip title={n.programDescription}>
              <Link component="button"  variant="body2" className={classes.custlink} onClick={goTo.bind(this, theProgramLink)}>
                  {n.programName}
              </Link>
            </Tooltip>
          </Typography>}
        </TableCell>
        <TableCell align="right">
          {!repeat &&
          <Typography variant="body2">
            {tmBcSum}
          </Typography>}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{tmRebateSum}</Typography>
        </TableCell>

        <TableCell align="center"> 
          {n.customerAppStatus === 0 && !repeat &&
            <CheckCircle htmlColor={statusOk}/>
          }
          { n.customerAppStatus === 1 && !repeat &&
            <PauseCircleFilled htmlColor={statusPause}/>
          }
          { n.customerAppStatus === 2 && !repeat &&
            <Cancel htmlColor={statusCancel}/>
          }
        </TableCell>
      </TableRow>
      {!repeat &&
      <TableRow>
        <TableCell style={ rowOpen ? {} : { padding: 0 }}/>
        <TableCell colSpan={6} style={ rowOpen ? {} : { padding: 0 }}>
          <Collapse in={rowOpen} timeout="auto" unmountOnExit>
            <Grid container spacing={2} justifyContent="flex-start">
              <Grid item style={{minWidth:'300px'}}>
                <Grid container spacing={0} direction="column" justifyContent="flex-start" >
                  <Grid item>
                    <InputLabel shrink style={{fontSize:"16px", marginBottom:"8px"}}>Primary contact</InputLabel>
                  </Grid>
                  <Grid item>
                    <Typography align="left" variant="body1" data-recording-sensitive>{customerContactName}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography  align="left" variant="body1" data-recording-sensitive>{n.customerContactEmail}</Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container spacing={0} direction="column" justifyContent="flex-start" >
                  <Grid item>
                    <InputLabel shrink style={{fontSize:"16px", marginBottom:"8px"}}>Billing address</InputLabel>
                  </Grid>
                  <Grid item>
                    <Typography  align="left" variant="body1" data-recording-sensitive>
                      {address.address1 == null || address.address1 === '' ? 'Not specified' : address.address1}
                    </Typography>
                  </Grid>
                  { address.address2 && address.address2 !== null && address.address2 !== 'null' &&
                  <Grid item>
                    <Typography align="left" variant="body1" data-recording-sensitive>{address.address2}</Typography>
                  </Grid>
                  }
                  { address.city && address.state && address.country && address.postalCode &&
                  <Grid item>
                    <Typography align="left" variant="body1" data-recording-sensitive><span>{address.city}</span>, <span>{address.state}</span>, <span>{address.country}</span> <span>{address.postalCode}</span></Typography>
                  </Grid>
                  }
                </Grid>
              </Grid>
            </Grid>
          </Collapse>
        </TableCell>
      </TableRow>
      }
    </React.Fragment>
  )
}

class ResellerSubscriptionsList extends React.Component {

  getCurrencyString(theValue) {
    if (isNaN(theValue))
      return '';

    let currencyString = Number(theValue)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

    return currencyString;
  }

  render() {

    const classes = this.props.classes;
    const thenodes = this.props.sl.nodes;
    
    //this is just "index+1" on the node map; currently only used for key.
    // let rowCounter = 1;

    // Get the summary data from the last entry
    let thisMonthBilled = 0;
    let thisMonthRebate = 0;

    let lastIdx = thenodes.length-1;
    if (lastIdx >= 0) {
      let lastNode = thenodes[lastIdx];
      thisMonthBilled = lastNode.ttlTmBcSum
      thisMonthRebate = lastNode.ttlTmRebateSum
    }

    // Reseller's view of customer subscriptions
    let thisMonthBilledSummary = this.getCurrencyString(thisMonthBilled);
    let thisMonthRebateSummary = this.getCurrencyString(thisMonthRebate);

    let previousAppName = null;
    let previousCustomerName = null;

    return (
      <div className={classes.paper}>
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead className={classes.tblHead}>
              <TableRow className={classes.tblHeadRow}>
                <TableCell className={classes.tblHeadCell}></TableCell>
                <TableCell className={classes.tblHeadCell}></TableCell>
                <TableCell className={classes.tblHeadCell}></TableCell>
                <TableCell className={classes.tblHeadCell}></TableCell>
                <TableCell className={classes.tblHeadCellB}>Billed&nbsp;last&nbsp;30&nbsp;days</TableCell>
                <TableCell className={classes.tblHeadCellB}>Commissions&nbsp;last&nbsp;30&nbsp;days</TableCell>
                <TableCell className={classes.tblHeadCell}/>
              </TableRow>
              <TableRow className={classes.tblHeadRow3}>
                <TableCell></TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>App</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right"><Typography variant="subtitle2">{thisMonthBilledSummary}</Typography></TableCell>
                <TableCell align="right"><Typography variant="subtitle2">{thisMonthRebateSummary}</Typography></TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              { thenodes.map((n, index) => {
                //broke out the row render setup into Function component; this for utilizing collapse per row.
                return (
                  <SubscriptionRow key={index+1} n={n}
                    classes={classes} getCurrencyString={this.getCurrencyString}
                    previousAppName={previousAppName} previousCustomerName={previousCustomerName} {...this.props} />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }
}

ResellerSubscriptionsList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default createFragmentContainer(withStyles(styleSheet)(ResellerSubscriptionsList), { sl: graphql`
  fragment ResellerSubscriptionsList_sl on ResellerCustomerSubscriptionChargesConnection {
    nodes {
      appName
      appSku
      appLogo
      customerAccountName
      customerFirstName
      customerLastName
      customerContactEmail
      customerAddress1
      customerAddress2
      customerCity
      customerState
      customerCountry
      customerPostalCode
      tmBcSum
      ttlTmBcSum
      tmRebateSum
      ttlTmRebateSum
      customerAppStatus
      programId
      programName
      programDescription
    }
  }
`});
